import {IMessageData, IObjectData, ISearchObjectParams, ISyncField, TSyncfield} from "../../../index";
import * as ko from "knockout";
import {IComponentDialogParams, IDialog} from "../../modal/component";
import PromiseRequest from "../../../req/PromiseRequest";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {_} from 'lodash';
import {
    createErrorMessage,
    downloadCSV,
    getObjectDefinition,
    getObjectType,
    getSourceTranslation,
    getSyncField
} from "../../../util/misc";
import {ObjectTypeId} from "../../../constants";
import {storeObservable} from "../../../util/sessionstore";
import pubsub from "../../../pubsub";
import {IRequestObject} from "../../../req/index";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {ISelectSyncField} from "./index";
import {xhrFactory} from "../../../util/request";
import {PLEASE_WAIT_MESSAGE, REQUEST_MESSAGE} from "../../../constants/Language";
import {nsRuleset} from "../../ruleset/nsRuleset";
import {moment} from 'moment';
import {convertToServerString} from "../../../util/datetime";
import {global} from "../../../appContext";
import export_csvHtml from './export.csv.html?raw';

export let template: string = export_csvHtml;

interface IParams {
    dialog: IDialog
    searchObjectParams: ISearchObjectParams
    data: KnockoutObservableArray<IObjectData>
}

enum Step {
    Init,
    SelectSyncFields,
    Downloading,
    Success,
    Error,
}

export class viewModel {

    Step = Step;
    step: KnockoutObservable<Step | null> = ko.observable(null);
    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected data!: IObjectData[];

    private readonly reqObj_getsyncfields: IRequestObject;
    private reqSearchObject = new PromiseRequest("searchobject");
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    percentComplete: KnockoutObservable<number | null> = ko.observable(null);

    private selectedSyncfields: KnockoutObservableArray<ISelectSyncField> = ko.observableArray<ISelectSyncField>([]);

    protected objecttypeid: ObjectTypeId;

    constructor(protected params: IParams) {
        this.kosub.subscribe(this.step, this.updateStep.bind(this));
        this.objecttypeid = params.searchObjectParams.objecttypeid;
        this.reqObj_getsyncfields = nsSyncFields.getSyncFields(this.objecttypeid);
        this.step(Step.Init);
        pubsub.on(this, "before.close.dialog", (e: any, dialogData: IComponentDialogParams) => {
            if (dialogData.dialog == this.params.dialog) {
                this.dispose();
            }
        });
    }

    protected updateStep() {
        switch (this.step()) {

            case Step.Init:
                this.messageData({message: REQUEST_MESSAGE});
                this.reqObj_getsyncfields.request
                    .then((data: ISyncField[]) => {
                        const storeId = `search.object.syncfields${this.objecttypeid}` as `search.object.syncfields${ObjectTypeId}`;
                        const searchColumnKeys = storeObservable(storeId)()
                        const d: ISelectSyncField[] = data.map(sf => {
                            const isSelected = searchColumnKeys.includes(sf.name);
                            return {
                                syncfield: sf,
                                selected: ko.observable(isSelected),
                            }
                        });
                        this.selectedSyncfields(d);
                        this.step(Step.SelectSyncFields);
                    })
                    .catch(() => {
                        this.step(Step.Error);
                    });
                break;

            case Step.SelectSyncFields:
                this.messageData(null);
                break;

            case Step.Downloading:
                this.messageData({message: PLEASE_WAIT_MESSAGE});

                const params = _.cloneDeep(this.params.searchObjectParams)
                params.syncfieldids = this.selectedSyncfields().filter(o => o.selected()).map(o => o.syncfield.syncfieldid).join(',')

                Promise.resolve()
                    .then(() => {
                        if (this.params.data().length) {
                            this.data = this.params.data();
                            const ruleset = nsRuleset.generateRuleset({
                                objecttype: getObjectType(this.objecttypeid),
                                rules: [nsRuleset.generateRule({
                                    argument: this.data.map(o => o.id).join(','),
                                    argumentoperator: 'Inneholder en av',
                                    attribute: "Id"
                                })]
                            })
                            params.jruleset = JSON.stringify(ruleset)
                        }

                        return this.reqSearchObject
                            .request(params, false, {
                                timeout: 30 /*min*/ * 60 /*sec*/ * 1000 /*ms*/,
                                xhr: xhrFactory(this.percentComplete as KnockoutObservable<number>, this.messageData as KnockoutObservable<IMessageData>),
                            })
                            .then((data: IObjectData[]) => {
                                this.data = data;
                                // FIXME there might be a problem with browser security not allowing downloading without a proper user interaction (e.g. a button click)
                                this.downloadCSV();
                                this.step(Step.Success);
                            })
                            .catch((reason: string) => {
                                this.step(Step.Error);
                                this.messageData(createErrorMessage(String(reason)));
                            });
                    })
                break;

            case Step.Success:
                this.messageData({message: `((download completed))<br><br>((note: file may contain personal data))`})
                break
            case Step.Error:
                break;

        }
    }

    private getSessionStoreKey(name: string): string {
        return "syncfield." + this.objecttypeid + "." + name;
    }

    protected downloadCSV() {
        const keys = this.selectedSyncfields().filter((item: ISelectSyncField) => item.selected()).map((item: ISelectSyncField) => item.syncfield.name);
        const csv = keys.map(key => `"${key}"`).join(";") + "\n"
            + this.data.map((obj: IObjectData) => {
                return keys
                    .map((key: TSyncfield) => {
                        const val = this.getObjectValue(key, obj);
                        return val ? val.replace(/"/gm, '""') : "";
                    })
                    .map(val => `"${val}"`)
                    .join(";");
            }).join("\n");
        downloadCSV(csv, this.getFileName());
    }

    private getObjectValue(key: TSyncfield, obj: IObjectData): string {
        // either a value directly on the object
        switch (key) {
            case "Id":
                return String(obj.id);
            default:
                // ...or the syncfield value
                const sf = getSyncField(key, obj.syncfields)
                let val: string
                if (typeof sf?.value == 'string') {
                    if (sf.fieldtype == 'DateTime') {
                        const m = moment(sf.value)
                        val = convertToServerString(m) ?? ''
                    } else {
                        val = sf.value
                    }
                } else {
                    val = ''
                }
                return val
        }
    }

    protected getFileName(): string {
        const objecttype = getObjectDefinition(this.objecttypeid)!.name.toLowerCase()
        const orgnr = global.login.selectedOrganizationId() ?? ''
        const date = moment().format('YYYY-MM-DD')
        return `eadm.${objecttype}.${orgnr}_${date}.csv`
    }

    /*******************************************************************************************************************
     * View interaction
     ******************************************************************************************************************/

    onCancelClick() {
        this.dispose();
    }

    onDownloadClick() {
        this.step(Step.Downloading);
    }

    /*******************************************************************************************************************
     * View helpers / computed properties
     ******************************************************************************************************************/

    isStep(...steps: number[]): boolean {
        return steps.some((step: Step) => step == this.step());
    }

    selectAllSyncFields = ko.computed({
        read: () => {
            return this.selectedSyncfields().every((item: ISelectSyncField) => item.selected());
        },
        write: (val: boolean) => {
            this.selectedSyncfields().forEach((item: ISelectSyncField) => item.selected(val));
        }
    });

    isIndeterminate(): boolean {
        return !this.selectAllSyncFields() && this.selectedSyncfields().some((item: ISelectSyncField) => item.selected())
    }

    isDownloadable = ko.pureComputed(() => this.selectedSyncfields().some((item: ISelectSyncField) => item.selected()));

    private sortedSyncFields = ko.pureComputed(() => {
        return _.sortBy(this.selectedSyncfields(), (ssf: ISelectSyncField) => getSourceTranslation(ssf.syncfield));
    });

    /*******************************************************************************************************************
     * Misc.
     ******************************************************************************************************************/

    dispose() {
        pubsub.off(this);
        this.reqSearchObject.abort();
        this.reqObj_getsyncfields.promise.abort();
    }

}
